import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudSync as CloudSyncIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import { licensesApi, Product, Subscription } from '@/api/licenses-api';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProductData = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch product details
      const productData = await licensesApi.getProduct(id);
      setProduct(productData);
      
      // We would need a proper API endpoint to get subscriptions by product
      // For now, let's simulate with empty array
      setSubscriptions([]);
    } catch (err) {
      console.error('Error fetching product data:', err);
      setError('Failed to load product data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
          sx={{ mt: 2 }}
        >
          Back to Products
        </Button>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">Product not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
          sx={{ mt: 2 }}
        >
          Back to Products
        </Button>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={`Product: ${product.description}`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Products', href: '/products' },
          { label: product.description },
        ]}
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8}}>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12}}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h6">Product Information</Typography>
                      {product.metadata?.status && (
                        <Chip 
                          label={product.metadata.status === 'active' ? 'Active' : 'Inactive'} 
                          color={product.metadata.status === 'active' ? 'success' : 'default'}
                        />
                      )}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">{product.description}</Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Sherweb SKU
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1">{product.sherwebSku}</Typography>
                      <Tooltip title="Synced with Sherweb">
                        <CloudSyncIcon color="primary" fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Chargebee Item Price ID
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1">{product.chargebeeItemPriceId}</Typography>
                      <Tooltip title="Linked with Chargebee">
                        <LinkIcon color="primary" fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Price
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {product.metadata?.price ? `$${product.metadata.price.toFixed(2)}` : 'Not set'}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Added Date
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {new Date(product.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4}}>
              <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Product Metadata</Typography>
                  
                  {product.metadata && Object.keys(product.metadata).length > 0 ? (
                    <Box component="pre" sx={{ 
                      overflowX: 'auto', 
                      backgroundColor: '#f5f5f5', 
                      p: 2, 
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}>
                      {JSON.stringify(product.metadata, null, 2)}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No metadata available for this product.
                    </Typography>
                  )}
                </CardContent>
              </Card>
              
              {user?.role !== 'customer' && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/subscriptions/new', { state: { productId: product.productId } })}
                >
                  Create Subscription with this Product
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active Subscriptions Using This Product
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {subscriptions.length > 0 ? (
            <Typography>
              Subscription list would appear here, but requires API endpoint to get subscriptions by product
            </Typography>
          ) : (
            <Alert severity="info">
              No active subscriptions found for this product.
            </Alert>
          )}
        </CardContent>
      </Card>
    </>
  );
}
